import { React, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'

import './Sitemap.scss'

import { useDocumentTitle, useDocumentConfig } from 'hooks/useDocument'
import { useFetch } from 'hooks/useFetch'
import RefletRefletBanner from 'components/RefletRefletBanner'

const Sitemap = () => {
    useDocumentTitle(`Plan du site`)
    useDocumentConfig(`blue`, true)

    const [projects, setProjects] = useState([])
    const [museumProjects, setMuseumProjects] = useState([])
    const { loading, data } = useFetch(`/realisations/listing.json`, 'json')

    useEffect(() => {
        if (data === null) {
            return
        }

        const normalProjects = data.filter(p => p.sector !== 'museum')
        const musealProjects = data.filter(p => p.sector === 'museum')
        
        setProjects(normalProjects)
        setMuseumProjects(musealProjects)
    }, [data])

    return (
        <motion.main
            className="sitemap"
            initial={{ opacity: 0, transition: { duration: 0.3 } }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.3 } }}
        >
            <h1>Plan du site</h1>
            <p className="light">
                Découvrez l'ensemble de nos pages et services. Retrouvez également nos mentions légales et notre présentation.
            </p>

            <section className="sitemap-section">
                <h2>Pages principales</h2>
                <div className="sitemap-grid">
                    <Link className="sitemap-card" to={`${process.env.PUBLIC_URL}/`}>
                        <h3>Accueil</h3>
                        <p className="light">Découvrez BIG et Nouf, votre agence web spécialisée dans la création de sites web utiles et sensibles.</p>
                    </Link>
                    <Link className="sitemap-card" to={`${process.env.PUBLIC_URL}/notre-agence`}>
                        <h3>Notre agence</h3>
                        <p className="light">Rencontrez notre équipe passionnée par le développement web et l'innovation technologique.</p>
                    </Link>
                    <Link className="sitemap-card" to={`${process.env.PUBLIC_URL}/nos-services`}>
                        <h3>Nos services</h3>
                        <p className="light">Explorez nos différents services de développement web et d'accompagnement digital.</p>
                    </Link>
                    <Link className="sitemap-card" to={`${process.env.PUBLIC_URL}/realisations-sur-mesure`}>
                        <h3>Réalisations</h3>
                        <p className="light">Découvrez nos projets récents et nos créations sur mesure pour différents secteurs.</p>
                    </Link>
                    <Link className="sitemap-card" to={`${process.env.PUBLIC_URL}/nous-contacter`}>
                        <h3>Contact</h3>
                        <p className="light">Prenez contact avec notre équipe pour discuter de votre projet web.</p>
                    </Link>
                </div>
            </section>

            <section className="sitemap-section">
                <h2>Services détaillés</h2>
                <div className="sitemap-grid">
                    <Link className="sitemap-card" to={`${process.env.PUBLIC_URL}/services/integration-animation`}>
                        <h3>Intégration & Animation</h3>
                        <p className="light">Développement d'interfaces utilisateur dynamiques et interactives avec les dernières technologies web.</p>
                    </Link>
                    <Link className="sitemap-card" to={`${process.env.PUBLIC_URL}/services/applications-web`}>
                        <h3>Applications Web</h3>
                        <p className="light">Création d'applications web sur mesure adaptées à vos besoins métier spécifiques.</p>
                    </Link>
                    <Link className="sitemap-card" to={`${process.env.PUBLIC_URL}/services/wordpress-ecommerce`}>
                        <h3>WordPress & E-commerce</h3>
                        <p className="light">Développement de sites WordPress et solutions e-commerce performantes et sécurisées.</p>
                    </Link>
                    <Link className="sitemap-card" to={`${process.env.PUBLIC_URL}/services/tma-support`}>
                        <h3>TMA & Support</h3>
                        <p className="light">Tierce Maintenance Applicative et support technique pour assurer la pérennité de vos projets.</p>
                    </Link>
                    <Link className="sitemap-card" to={`${process.env.PUBLIC_URL}/services/projets-creatifs-iot`}>
                        <h3>Projets Créatifs & IoT</h3>
                        <p className="light">Développement de projets innovants intégrant les objets connectés et les nouvelles technologies.</p>
                    </Link>
                </div>
            </section>

            <section className="sitemap-section">
                <h2>Nos réalisations</h2>
                <div className="sitemap-grid">
                    {!loading && projects.length > 0 && projects.map((project, i) => (
                        <Link key={i} className="sitemap-card" to={`${process.env.PUBLIC_URL}/realisations/${project.id}`}>
                            <h3>{project.title}</h3>
                            <p className="light" dangerouslySetInnerHTML={{ __html: project.description }}></p>
                        </Link>
                    ))}
                </div>
            </section>

            {!loading && museumProjects.length > 0 && (
                <section className="sitemap-section">
                    <h2>Nos expériences muséales et culturelles</h2>
                    <p className="light sitemap-museum-intro">
                        Découvrez nos projets dédiés aux musées, bibliothèques et institutions culturelles. 
                        Ces réalisations font partie de notre expertise spécialisée chez <strong>Reflet Reflet</strong>.
                    </p>
                    
                    <RefletRefletBanner />
                    
                    <div className="sitemap-grid">
                        {museumProjects.map((project, i) => (
                            <Link key={i} className="sitemap-card" to={`${process.env.PUBLIC_URL}/realisations/${project.id}`}>
                                <h3>{project.title}</h3>
                                <p className="light" dangerouslySetInnerHTML={{ __html: project.description }}></p>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            <section className="sitemap-section">
                <h2>Informations légales</h2>
                <div className="sitemap-grid">
                    <Link className="sitemap-card" to={`${process.env.PUBLIC_URL}/mentions`}>
                        <h3>Mentions légales</h3>
                        <p className="light">Consultez nos mentions légales obligatoires et informations sur l'éditeur du site.</p>
                    </Link>
                </div>
            </section>

            <section className="sitemap-section sitemap-section--about">
                <h2>À propos de BIG et Nouf</h2>
                <p className="light sitemap-about-intro">
                    <strong>BIG et Nouf</strong> est une agence web spécialisée dans la création de sites web sur mesure, 
                    basée à Arras. Nous accompagnons les entreprises, institutions culturelles et collectivités 
                    dans leur transformation digitale en proposant des solutions web innovantes et adaptées.
                </p>
                
                <div className="sitemap-grid sitemap-grid--about">
                    <div className="sitemap-card">
                        <h3>Notre expertise technique</h3>
                        <p className="light">
                            Nous maîtrisons les technologies modernes du web : <strong>Nuxt.js</strong>, <strong>React.js</strong>, <strong>Node.js</strong>, 
                            <strong>WordPress</strong>, <strong>Three.js</strong> pour la 3D, les outils d'animation web avancés, 
                            le développement d'<strong>installations interactives</strong>, l'<strong>IoT</strong> (Arduino, Raspberry Pi), etc.
                        </p>
                    </div>
                    
                    <div className="sitemap-card">
                        <h3>Secteurs d'activité</h3>
                        <p className="light">
                            Nous intervenons particulièrement dans le <strong>secteur culturel</strong> (musées, bibliothèques, 
                            institutions patrimoniales), l'<strong>e-commerce</strong>, et accompagnons les <strong>entreprises 
                            innovantes</strong> dans leurs projets digitaux créatifs.
                        </p>
                    </div>
                    
                    <div className="sitemap-card">
                        <h3>Nos valeurs</h3>
                        <p className="light">
                            Nous croyons en la création de sites web <strong>utiles et sensibles</strong>, qui allient 
                            fonctionnalité et émotion. Notre approche privilégie l'accessibilité, la performance et 
                            l'expérience utilisateur optimale sur tous les supports.
                        </p>
                    </div>
                    
                    <div className="sitemap-card">
                        <h3>Zone d'intervention</h3>
                        <p className="light">
                            Basés à <strong>Arras</strong>, nous intervenons dans toute la région des <strong>Hauts-de-France</strong> et
                            accompagnons des clients partout en France. Nous proposons des rencontres en présentiel
                            dans notre région et des échanges à distance pour les projets nationaux.
                        </p>
                    </div>
                </div>
                
                <div className="sitemap-sceneartio">
                    <h3>SceneArtIO - Notre solution SaaS</h3>
                    <p className="light">
                        Nous développons <strong>SceneArtIO</strong>, une plateforme no-code qui permet de créer 
                        des applications interactives pour bornes tactiles. Interface intuitive, glisser-déposer, 
                        modèles sectoriels - créez vos applications en quelques heures au lieu de semaines.
                    </p>
                    <p className="light">
                        <a href="https://sceneartio.com/" className="link" target="_blank" rel="noreferrer noopener">
                            Découvrir SceneArtIO
                        </a>
                    </p>
                </div>
            </section>
        </motion.main>
    )
}

export default Sitemap